import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';

import { isBackendErrorEvent } from '../../../backend/BackendErrorEvent';
import { RequestWarning, RequestWarningType } from '../../utils/requests/RequestWarning';
import { GcdmCustomerService } from '../clients/gcdm/gcdm-customer.service';
import { RetailCrmService } from '../clients/retail-crm/retail-crm.service';
import { Customer } from '../model/customer.model';
import { ownsMasterDataInGcdm } from './customer-source';

/** The system the master data of an edited customer was written to. */
export type CustomerUpdateTarget = 'GCDM' | 'RETAIL_CRM';

/**
 * Why a write failed, in the granularity the user can act on.
 *
 * `CONFLICT` is the only retryable one: GCDM locks a GCID while an update is running, so the
 * same payload usually succeeds on a second attempt (CUSTAPP-613).
 */
export type CustomerUpdateFailureReason = 'CONFLICT' | 'REJECTED' | 'NOT_FOUND' | 'TYPE_NOT_SUPPORTED' | 'UNKNOWN';

export type CustomerUpdateResult =
  | { status: 'SUCCESS'; target: CustomerUpdateTarget }
  | { status: 'FAILED'; target: CustomerUpdateTarget; reason: CustomerUpdateFailureReason; error: unknown };

/** Maps the backend error types of `PUT /gcdm/customers/{gcid}` onto the reasons above. */
const GCDM_ERROR_REASONS: Record<string, CustomerUpdateFailureReason> = {
  GCDM_CUSTOMER_UPDATE_CONFLICT: 'CONFLICT',
  GCDM_CUSTOMER_UPDATE_REJECTED: 'REJECTED',
  GCDM_CUSTOMER_UPDATE_FAILED: 'UNKNOWN',
  GCDM_CUSTOMER_NOT_FOUND: 'NOT_FOUND',
  GCDM_CUSTOMER_TYPE_NOT_SUPPORTED: 'TYPE_NOT_SUPPORTED',
};

/**
 * Routes an edited customer to the system that owns its master data (CUSTAPP-613).
 *
 * ```
 * inSyncWithDirectSales && gcid -> NSC Database (GCDM)
 * otherwise                     -> Retail CRM   (unchanged behaviour)
 * ```
 *
 * The decision lives here and nowhere else, so the edit flow and the sync flow
 * ({@link GcdmSyncService}) cannot drift apart.
 *
 * Two deliberate non-behaviours:
 *
 * - **Dealer consents are not written here.** They are Retail CRM's regardless of routing and are
 *   saved through `DealerConsentService` / `RetailCrmService.saveDealerConsents`, which the edit
 *   dialog does not touch - `updateAccount` strips them too.
 * - **A synced customer without a GCID falls back to Retail CRM.** Blocking would leave such a
 *   customer uneditable *and* unsyncable, since the sync button is disabled by the very flag that
 *   caused the fallback. The `warn` makes the population measurable instead.
 */
@Injectable({
  providedIn: 'root',
})
export class CustomerUpdateService {
  private readonly gcdmCustomerService = inject(GcdmCustomerService);
  private readonly retailCrmService = inject(RetailCrmService);
  private readonly logger = inject(NGXLogger);

  async updateCustomer(customer: Customer): Promise<CustomerUpdateResult> {
    return this.writesToGcdm(customer) ? this.updateInGcdm(customer.gcid!, customer) : this.updateInRetailCrm(customer);
  }

  /** Exposed so callers can label the dialog or skip Retail-only fields before saving. */
  writesToGcdm(customer: Customer): boolean {
    if (ownsMasterDataInGcdm(customer)) {
      return true;
    }

    // A GCID alone is only a link, not ownership: an account that is not flagged as synced stays
    // Retail CRM's. Only the synced-without-GCID case is worth reporting - it is the one where the
    // routing silently falls back.
    if (customer.inSyncWithDirectSales && !customer.gcid) {
      this.logger.warn(
        `#CDFTF0280 Customer ${customer.accountNumber} is flagged as synced with the NSC Database but has no GCID. ` +
          `Falling back to Retail CRM; this customer cannot be edited in the NSC Database until it is linked.`,
      );
    }

    return false;
  }

  private async updateInGcdm(gcid: string, customer: Customer): Promise<CustomerUpdateResult> {
    try {
      await this.gcdmCustomerService.updateSyncedCustomer(gcid, customer);
      return { status: 'SUCCESS', target: 'GCDM' };
    } catch (error) {
      const reason = this.reasonOf(error);
      this.logger.error(
        `#CDFTF0281 Could not update the synced customer ${customer.accountNumber} in the NSC Database ` +
          `under GCID ${gcid} (${reason}). Retail CRM was left untouched.`,
        error,
      );
      return { status: 'FAILED', target: 'GCDM', reason, error };
    }
  }

  private async updateInRetailCrm(customer: Customer): Promise<CustomerUpdateResult> {
    try {
      await this.retailCrmService.updateAccount(customer);
      return { status: 'SUCCESS', target: 'RETAIL_CRM' };
    } catch (error) {
      this.logger.error(`#CDFTF0282 Could not update the customer ${customer.accountNumber} in Retail CRM.`, error);
      return { status: 'FAILED', target: 'RETAIL_CRM', reason: 'UNKNOWN', error };
    }
  }

  private reasonOf(error: unknown): CustomerUpdateFailureReason {
    // The client already turns a 409 into a warning, so the conflict never reaches here as HTTP.
    if (error instanceof RequestWarning && error.type === RequestWarningType.CONCURRENT_UPDATE) {
      return 'CONFLICT';
    }

    const body: unknown = error instanceof HttpErrorResponse ? error.error : error;
    if (isBackendErrorEvent(body) && body.type) {
      return GCDM_ERROR_REASONS[body.type] ?? 'UNKNOWN';
    }

    return 'UNKNOWN';
  }
}
